import { css } from 'react-emotion';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { reduxForm, formValueSelector } from 'redux-form';
import validate from './validate';
import StepTitle from '../../../common/StepTitle';

import SkuCardVirtualizedList from '../SetupPricingTermStep/SkuCardVirtualizedList';
import SkuCard from '../shared/SkuCard/SkuCard';
import { TermTypeLabel } from '../../../../constants/termTypes';
import { NEW, EXISTING } from './SelectPartyStep';
import { getDisplayDate } from '../../../../utilities/displayFunctions';
import Button from '../../../common/Button';
import { withTranslation } from 'react-i18next';

const divStyle = css({
  marginBottom: '50px'
});

const prevButtonStyle = css({
  float: 'left'
});

const nextButtonStyle = css({
  float: 'right'
});

const section = css`
  margin-bottom: 20px;
`;

const sectionHeaderStyle = css({
  marginBottom: '30px',
  textAlign: 'center',
  fontSize: '20px'
});

const tableHeaderStyle = css({
  borderBottom: '1px solid #E8E8E8',
  margin: '0 0 10px'
});

const dlStyle = css({
  marginBottom: '0'
});

const tableSpacing = css({
  marginBottom: '20px'
});

class ReviewStep extends Component {
  static propTypes = {
    onPrev: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    seller: PropTypes.object.isRequired,
    buyer: PropTypes.object.isRequired,
    skus: PropTypes.object.isRequired,
    comment: PropTypes.string,
    dateSelection: PropTypes.object.isRequired,
    newContract: PropTypes.func.isRequired,
    selectedContract: PropTypes.object,
    updateContract: PropTypes.func.isRequired
  };

  skuCardRenderer = skuInfo => <SkuCard skuInfo={skuInfo} />;

  createContract = () => {
    const { creationType } = this.props;

    switch (creationType) {
      case NEW:
        this.newContract();
        break;
      case EXISTING:
        this.updateContract();
        break;
      default:
    }
  };

  newContract = () => {
    const { buyer, seller, skus, contractName, comment, dateSelection, newContract } = this.props;

    const endDate = dateSelection.noEndDateChecked ? null : dateSelection.endDate;

    const productPricingTerm = {
      termType: TermTypeLabel.LIST_PRICE,
      startDate: dateSelection.startDate,
      endDate: endDate,
      termName: contractName,
      comment
    };

    const initiator = { type: buyer.type, id: buyer.id };
    const acceptor = { type: seller.type, id: seller.id };

    const pricingTerm = {
      pricingTermInfo: productPricingTerm,
      selectedSkus: skus,
      initiator,
      acceptor,
      buyer: initiator,
      seller: acceptor,
      toPendingStatus: true
    };

    newContract(pricingTerm);
  };

  updateContract = () => {
    const { selectedContract, skus, updateContract } = this.props;
    const skuList = Object.keys(skus);

    updateContract(selectedContract, skuList);
  };

  getContractName() {
    const { creationType, selectedContract, contractName } = this.props;

    return creationType === EXISTING ? selectedContract.additionalData.name : contractName;
  }

  getEndDateDisplay() {
    const { creationType, dateSelection, selectedContract, t } = this.props;

    let endDate;

    if (creationType === NEW && dateSelection.noEndDateChecked) {
      endDate = null;
    } else if (creationType === NEW && !dateSelection.noEndDateChecked) {
      endDate = dateSelection.endDate;
    } else if (creationType === EXISTING) {
      endDate = selectedContract.effectiveEndDate;
    }

    return !endDate ? t('common:commonTerminology.indefinite') : getDisplayDate(endDate);
  }

  getStartDateDisplay() {
    const { creationType, dateSelection, selectedContract } = this.props;

    const startDate = creationType === EXISTING ? selectedContract.effectiveStartDate : dateSelection.startDate;

    return getDisplayDate(startDate);
  }

  getSkuCardTitle() {
    const { creationType, t } = this.props;

    return creationType === NEW
      ? t('createContract:review.productsInContract')
      : t('createContract:review.productsToAddContract');
  }

  render() {
    const { onPrev, seller, buyer, skus, handleSubmit, t } = this.props;

    const selectedSkuCards = (
      <SkuCardVirtualizedList skuCardRenderer={this.skuCardRenderer} skuCardList={Object.values(skus)} height={400} />
    );

    return (
      <form onSubmit={handleSubmit(this.createContract)}>
        <StepTitle
          icon="finalize-contract-l"
          title={t('createContract:review.header')}
          description={t('createContract:review.description')}
        />
        <div>
          <div className="row">
            <div className={`col-sm-12 col-md-6 ${section}`}>
              <h4 className={sectionHeaderStyle}>{t('createContract:review.contractDetailsLabel')}</h4>
              <dl className={`dl-horizontal ${dlStyle}`}>
                <div className={tableSpacing}>
                  <header className={tableHeaderStyle}>
                    <h5>{t('createContract:review.generalDetails', { name: this.getContractName() })}</h5>
                  </header>
                  <dt>{t('common:commonTerminology.seller')}</dt>
                  <dd>{seller.name}</dd>
                  <dt>{t('common:commonTerminology.buyer')}</dt>
                  <dd>{buyer.name}</dd>
                  <dt>{t('common:commonTerminology.effectiveStartDate')}</dt>
                  <dd>{this.getStartDateDisplay()}</dd>
                  <dt>{t('common:commonTerminology.effectiveEndDate')}</dt>
                  <dd>{this.getEndDateDisplay()}</dd>
                </div>
              </dl>
            </div>
            <div className={`col-sm-12 col-md-6 ${section}`}>
              <h4 className={sectionHeaderStyle}>{this.getSkuCardTitle()}</h4>
              <div>{selectedSkuCards}</div>
            </div>
          </div>
        </div>
        <div className={divStyle}>
          <span className={prevButtonStyle}>
            <Button className="btn btn-outline-primary" onClick={onPrev}>
              {t('common:commonWords.prev')}
            </Button>
          </span>
          <span className={nextButtonStyle}>
            <Button className="btn btn-primary">{t('contractActions:sendForApproval')}</Button>
          </span>
        </div>
      </form>
    );
  }
}

function mapStateToProps(state) {
  const formSelector = formValueSelector('buyerCreateFlow');

  return {
    seller: formSelector(state, 'seller'),
    buyer: formSelector(state, 'buyer'),
    skus: formSelector(state, 'skus'),
    dateSelection: formSelector(state, 'dateSelection'),
    creationType: formSelector(state, 'creationType'),
    contractName: formSelector(state, 'contractName'),
    comment: formSelector(state, 'comment'),
    selectedContract: formSelector(state, 'selectedContract')
  };
}

ReviewStep = connect(
  mapStateToProps,
  null
)(ReviewStep);

export default reduxForm({
  form: 'buyerCreateFlow',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  validate
})(withTranslation()(ReviewStep));
